import { useParams, Link } from 'react-router-dom';
import { categories, animeList } from '@/mocks/anime';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AnimeCard from '@/pages/home/components/AnimeCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredAnime = category ? animeList.filter((a) => a.category === category.id) : [];

  if (!category) {
    return (
      <>
        <SEOHead
          title="分类未找到 - 91动漫"
          description="抱歉，您访问的分类页面不存在。请浏览91动漫的其他分类。"
          keywords="91动漫,动漫分类"
        />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium text-sm whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const catName = category.name;
  const catDesc = category.description;

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${catName}动漫 - 91动漫${catName}番剧推荐`,
        description: `91动漫${catName}分类 - ${catDesc} 海量高清正版${catName}动漫在线观看`,
        url: `https://www.91anime.com/category/${slug}`,
        isPartOf: { '@id': 'https://www.91anime.com/#website' },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${catName}动漫 - 91动漫${catName}番剧推荐 | 高清在线观看`}
        description={`91动漫${catName}分类 - ${catDesc} 精选${catName}动漫番剧，高清正版在线观看，支持免费追番。91动漫为您推荐最热${catName}动漫。`}
        keywords={`91动漫,${catName}动漫,${catName}番剧,高清${catName}动漫,${catName}动漫推荐,91动漫${catName}`}
        canonicalUrl={`https://www.91anime.com/category/${slug}`}
        structuredData={structuredData}
      />

      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '分类', path: '/category/rexue' },
            { label: catName },
          ]}
        />

        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-lg`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{catName}动漫</h1>
              </div>
              <p className="text-sm text-foreground-500 ml-13">{catDesc}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    c.id === category.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {c.name}
                </Link>
              ))}
            </div>

            {filteredAnime.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredAnime.map((anime) => (
                  <AnimeCard key={anime.id} {...anime} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无番剧，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}